import { useEffect } from 'react';
import socket from './socket';
import api from './domain/api';

// Helper function for base64 VAPID key conversion
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export default function AuthenticatedEffects({ isAuthenticated, user }) {
  useEffect(() => {
    if (!isAuthenticated) return;
    socket.connect();
    socket.on('connect', () => {
      console.log('✅ Socket connected:', socket.id);
    });
    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated]);

  // Setup Push Notifications with comprehensive debugging
  useEffect(() => {
    if (!isAuthenticated || !user?._id) return;

    const setupPushNotifications = async () => {
      console.log('🔍 [PUSH DEBUG] Starting push notification setup...');

      // Step 1: Check browser support
      if (!('serviceWorker' in navigator)) {
        console.error('❌ [PUSH DEBUG] Service Worker not supported');
        return;
      }

      if (!('PushManager' in window)) {
        console.error('❌ [PUSH DEBUG] Push Manager not supported');
        return;
      }

      if (!('Notification' in window)) {
        console.error('❌ [PUSH DEBUG] Notification API not supported');
        return;
      }

      console.log('✅ [PUSH DEBUG] Browser supports Push Notifications');

      try {
        // Step 2: Register Service Worker
        console.log('📝 [PUSH DEBUG] Registering Service Worker...');
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/',
        });
        console.log('✅ [PUSH DEBUG] Service Worker registered:', registration);

        // Step 3: Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        console.log('✅ [PUSH DEBUG] Service Worker is ready');

        // Step 4: Check existing subscription
        console.log('🔍 [PUSH DEBUG] Checking for existing subscription...');
        let subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          console.log('📦 [PUSH DEBUG] Existing subscription found:', subscription);
        } else {
          console.log('❌ [PUSH DEBUG] No existing subscription, creating new one...');

          // Step 5: Subscribe to push
          const VAPID_KEY =
            'BJjQZ8rLj6OLAvRjfutfhUuXDeagSPIONExelI2sURevAaGgSvxOkJZ9tU4UBIYdR2L_oc8wPd6ISDI4pb-gxsg';

          try {
            const vapidArray = urlBase64ToUint8Array(VAPID_KEY);
            console.log('🔑 [PUSH DEBUG] VAPID key decoded successfully');

            subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: vapidArray,
            });

            console.log('✅ [PUSH DEBUG] Subscription created:', subscription);
          } catch (err) {
            console.error('❌ [PUSH DEBUG] Subscription creation failed:', err);
            throw err;
          }
        }

        // Step 6: Save subscription to backend
        if (subscription && user?._id) {
          console.log('💾 [PUSH DEBUG] Saving subscription to backend for userId:', user._id);

          try {
            const response = await api.post('/users/save-subscription', {
              userId: user._id,
              subscription: subscription.toJSON(),
            });

            console.log('✅ [PUSH DEBUG] Subscription saved successfully:', response.data);
          } catch (err) {
            console.error('❌ [PUSH DEBUG] Failed to save subscription:', err);
            if (err.response) {
              console.error('Backend error:', err.response.data);
            }
          }
        } else {
          console.warn('⚠️  [PUSH DEBUG] Missing subscription or userId:', {
            hasSubscription: !!subscription,
            userId: user?._id,
          });
        }

        // Step 7: Request notification permission if not already granted
        if (Notification.permission === 'default') {
          console.log('❓ [PUSH DEBUG] Requesting notification permission...');
          const permission = await Notification.requestPermission();
          console.log(`🔔 [PUSH DEBUG] Notification permission: ${permission}`);
        } else {
          console.log(`✅ [PUSH DEBUG] Notification permission already: ${Notification.permission}`);
        }
      } catch (error) {
        console.error('❌ [PUSH DEBUG] Setup failed:', error);
      }
    };

    setupPushNotifications();
  }, [isAuthenticated, user?._id]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') {
          console.log('✅ Notification permission granted');
        } else {
          console.warn('❌ Notification permission denied');
        }
      });
    }
  }, [isAuthenticated]);

  // useEffect(() => {
  //   if (!window.OneSignal) return;

  //   window.OneSignal.push(function () {
  //     window.OneSignal.getUserId().then(async (oneSignalId) => {
  //       if (oneSignalId) {
  //         try {
  //           await api.post('/api/save-onesignal-id', {
  //             userId: user?._id,
  //             oneSignalId,
  //           });
  //           console.log('✅ OneSignal ID saved:', oneSignalId);
  //         } catch (err) {
  //           console.error('❌ Failed to save OneSignal ID', err);
  //         }
  //       }
  //     });
  //   });
  // }, []);

  return null;
}
